import { setAccessToken } from '../container';
import type { BackendApiPort } from '../ports/BackendApiPort';
import type {
  BackendApiError,
  BackendMeeting,
  BackendParticipant,
  CreateMeetingParams,
  CreateShareLinkParams,
  EditMeetingParams,
  JoinMeetingParams,
  JoinMeetingResult,
  LoginParams,
  RegisterParams,
  ShareLink,
  TokenPair,
} from '@shared/types';

interface HttpBackendApiClientOptions {
  baseUrl: string;
  /** Returns the current access token, or null when signed out. Injected so this
   * adapter never owns auth state — that lives in whatever store composes it. */
  getAccessToken: () => string | null;
}

/**
 * fetch()-backed implementation of BackendApiPort, matching openapi.yaml.
 * The only file allowed to build request URLs / headers for this backend —
 * swap this for a different transport (e.g. generated OpenAPI SDK) here only.
 */
export class HttpBackendApiClient implements BackendApiPort {
  constructor(private readonly options: HttpBackendApiClientOptions) { }

  // --- auth ---

  async register(params: RegisterParams): Promise<{ id: string }> {
    return this.request('POST', '/auth/register', {
      email: params.email,
      password: params.password,
      display_name: params.displayName,
    });
  }

  async login(params: LoginParams): Promise<TokenPair> {
    const data = await this.request<{ access_token: string; refresh_token: string }>(
      'POST',
      '/auth/login',
      { email: params.email, password: params.password },
    );
    setAccessToken(data.access_token)
    return { accessToken: data.access_token, refreshToken: data.refresh_token };
  }

  async refresh(refreshToken: string): Promise<TokenPair> {
    const data = await this.request<{ access_token: string; refresh_token: string }>(
      'POST',
      '/auth/refresh',
      { refresh_token: refreshToken },
    );
    return { accessToken: data.access_token, refreshToken: data.refresh_token };
  }

  async logout(refreshToken: string): Promise<void> {
    await this.request('POST', '/auth/logout', { refresh_token: refreshToken }, true);
  }

  // --- meetings ---

  async createMeeting(params: CreateMeetingParams): Promise<BackendMeeting> {
    const data = await this.request<RawMeeting>('POST', '/meetings', {
      title: params.title,
      scheduled_at: params.scheduledAt ?? null,
    }, true);
    return mapMeeting(data);
  }

  async listMeetings(): Promise<BackendMeeting[]> {
    const data = await this.request<RawMeeting[]>('GET', '/meetings', undefined, true);
    return data.map(mapMeeting);
  }

  async getMeeting(meetingId: string): Promise<BackendMeeting> {
    const data = await this.request<RawMeeting>('GET', `/meetings/${meetingId}`, undefined, true);
    return mapMeeting(data);
  }

  async editMeeting(meetingId: string, params: EditMeetingParams): Promise<BackendMeeting> {
    const data = await this.request<RawMeeting>('PATCH', `/meetings/${meetingId}`, {
      title: params.title,
      scheduled_at: params.scheduledAt,
      clear_scheduled_at: params.clearScheduledAt,
    }, true);
    return mapMeeting(data);
  }

  async startMeeting(meetingId: string): Promise<BackendMeeting> {
    const data = await this.request<RawMeeting>('POST', `/meetings/${meetingId}/start`, undefined, true);
    return mapMeeting(data);
  }

  async endMeeting(meetingId: string): Promise<void> {
    await this.request('POST', `/meetings/${meetingId}/end`, undefined, true);
  }

  async cancelMeeting(meetingId: string): Promise<void> {
    await this.request('POST', `/meetings/${meetingId}/cancel`, undefined, true);
  }

  // --- joining ---

  async joinMeeting(meetingId: string, params: JoinMeetingParams): Promise<JoinMeetingResult> {
    const data = await this.request<{ token: string; participant_id: string; ws_ticket: string }>(
      'POST',
      `/meetings/${meetingId}/join`,
      {
        identity: params.identity,
        display_name: params.displayName,
        share_token: params.shareToken ?? null,
      },
      true,
    );
    return { token: data.token, participantId: data.participant_id, wsTicket: data.ws_ticket };
  }

  // --- participants ---

  async listParticipants(meetingId: string): Promise<BackendParticipant[]> {
    const data = await this.request<RawParticipant[]>(
      'GET',
      `/meetings/${meetingId}/participants`,
      undefined,
      true,
    );
    return data.map(mapParticipant);
  }

  approveParticipant(meetingId: string, participantId: string, authOverride?: string): Promise<void> {
    return this.postAction(meetingId, participantId, 'approve', authOverride);
  }

  denyParticipant(meetingId: string, participantId: string, authOverride?: string): Promise<void> {
    return this.postAction(meetingId, participantId, 'deny', authOverride);
  }

  promoteParticipant(meetingId: string, participantId: string, authOverride?: string): Promise<void> {
    return this.postAction(meetingId, participantId, 'promote', authOverride);
  }

  demoteParticipant(meetingId: string, participantId: string, authOverride?: string): Promise<void> {
    return this.postAction(meetingId, participantId, 'demote', authOverride);
  }

  muteParticipant(meetingId: string, participantId: string, authOverride?: string): Promise<void> {
    return this.postAction(meetingId, participantId, 'mute', authOverride);
  }

  unmuteParticipant(meetingId: string, participantId: string, authOverride?: string): Promise<void> {
    return this.postAction(meetingId, participantId, 'unmute', authOverride);
  }

  removeParticipant(meetingId: string, participantId: string, authOverride?: string): Promise<void> {
    return this.postAction(meetingId, participantId, 'remove', authOverride);
  }

  approveParticipantToStage(meetingId: string, participantId: string, authOverride?: string): Promise<void> {
    return this.postAction(meetingId, participantId, 'approve-to-stage', authOverride);
  }

  removeParticipantFromStage(meetingId: string, participantId: string, authOverride?: string): Promise<void> {
    return this.postAction(meetingId, participantId, 'remove-from-stage', authOverride);
  }

  // --- share links ---

  async createShareLink(meetingId: string, params: CreateShareLinkParams): Promise<ShareLink> {
    const data = await this.request<RawShareLink>(
      'POST',
      `/meetings/${meetingId}/share-links`,
      { role: params.role, expires_at: params.expiresAt ?? null },
      true,
    );
    return mapShareLink(data);
  }

  // --- internals ---

  private postAction(
    meetingId: string,
    participantId: string,
    action: string,
    authOverride?: string,
  ): Promise<void> {
    return this.request(
      'POST',
      `/meetings/${meetingId}/participants/${participantId}/${action}`,
      undefined,
      true,
      authOverride,
    ).then(() => undefined);
  }

  async lockRoom(meetingId: string, authOverride?: string): Promise<void> {
    await this.request('POST', `/meetings/${meetingId}/lock`, undefined, true, authOverride);
  }

  async unlockRoom(meetingId: string, authOverride?: string): Promise<void> {
    await this.request('POST', `/meetings/${meetingId}/unlock`, undefined, true, authOverride);
  }

  private async request<T>(
    method: string,
    path: string,
    body?: unknown,
    authenticated = false,
    authOverride?: string,
  ): Promise<T> {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };

    if (authenticated) {
      const token = authOverride ?? this.options.getAccessToken();
      if (!token) {
        throw this.toError(401, 'no-access-token', 'No access token available for this request.');
      }
      headers.Authorization = `Bearer ${token}`;
    }

    const response = await fetch(`${this.options.baseUrl.replace(/\/$/, '')}${path}`, {
      method,
      headers,
      body: body === undefined ? undefined : JSON.stringify(body),
    });

    if (response.status === 204) {
      return undefined as T;
    }

    const payload = await response.json().catch(() => null);

    if (!response.ok) {
      const code = payload?.error?.code ?? null;
      const message = payload?.error?.message ?? `Request failed with status ${response.status}`;
      throw this.toError(response.status, code, message);
    }

    if (payload === null) {
      console.error(`Expected a JSON body from ${method} ${path} but got none.`, response.status, 'empty-response-body');
    }

    return payload as T;
  }

  private toError(status: number, code: string | null, message: string): BackendApiError {
    return { status, code, message };
  }
}

// --- wire <-> domain mapping (snake_case API payloads -> camelCase domain types) ---

interface RawMeeting {
  id: string;
  host_id: string;
  title: string;
  status: BackendMeeting['status'];
  scheduled_at: string | null;
  started_at: string | null;
  ended_at: string | null;
  created_at: string;
  updated_at: string;
}

function mapMeeting(raw: RawMeeting): BackendMeeting {
  if (raw === null) {
    return {} as BackendMeeting;
  }
  return {
    id: raw.id,
    hostId: raw.host_id,
    title: raw.title,
    status: raw.status,
    scheduledAt: raw.scheduled_at,
    startedAt: raw.started_at,
    endedAt: raw.ended_at,
    createdAt: raw.created_at,
    updatedAt: raw.updated_at,
  };
}

interface RawParticipant {
  id: string;
  meeting_id: string;
  user_id: string | null;
  display_name: string;
  role: BackendParticipant['role'];
  status: BackendParticipant['status'];
  joined_at: string | null;
  left_at: string | null;
}

function mapParticipant(raw: RawParticipant): BackendParticipant {
  return {
    id: raw.id,
    meetingId: raw.meeting_id,
    userId: raw.user_id,
    displayName: raw.display_name,
    role: raw.role,
    status: raw.status,
    joinedAt: raw.joined_at,
    leftAt: raw.left_at,
  };
}

interface RawShareLink {
  id: string;
  meeting_id: string;
  token: string;
  role: ShareLink['role'];
  expires_at: string | null;
}

function mapShareLink(raw: RawShareLink): ShareLink {
  return {
    id: raw.id,
    meetingId: raw.meeting_id,
    token: raw.token,
    role: raw.role,
    expiresAt: raw.expires_at,
  };
}